/* eslint-disable max-lines */
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RefreshIcon from '@mui/icons-material/Refresh';
import TextField from 'common/forms/TextField';
import { isEmpty, pick } from 'lodash';
import { Form } from 'react-final-form';
import { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import {
  validateGreaterThan,
  validateLessThanOrEqual,
  validateQuantities,
} from 'common/forms/formValidations';
import { unMatchedFields } from 'common/utils';
import { PrescriptionReviewPayloadContext } from 'common/hooks/usePrescriptionReviewPayloadContext';

const EditFillModal = ({
  open,
  onClose,
  editablePrescriptionDetails,
  shippedDate,
  isReshipment,
  parentLineItemQuantity,
}) => {
  const {
    payload,
    saveFillChanges,
    undoFill,
    redoFill,
    resetFillChanges,
    canUndoFill,
    canRedoFill,
  } = useContext(PrescriptionReviewPayloadContext);

  const formRef = useRef(null);
  const prevPayloadRef = useRef(null);

  useEffect(() => {
    if (open && payload?.prescriptionDetails?.dispensedQuantity !== undefined) {
      prevPayloadRef.current = payload.prescriptionDetails.dispensedQuantity;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !formRef.current) {
      return;
    }

    const currentQuantity = payload?.prescriptionDetails?.dispensedQuantity;

    const desiredValues = {
      ...editablePrescriptionDetails,
      dispensedQuantity:
        currentQuantity !== undefined
          ? currentQuantity
          : editablePrescriptionDetails.dispensedQuantity,
    };

    const currentFormValues = formRef.current.getState().values || {};

    if (currentFormValues.dispensedQuantity !== desiredValues.dispensedQuantity) {
      formRef.current.initialize(desiredValues);
    }
  }, [payload?.prescriptionDetails?.dispensedQuantity, open, editablePrescriptionDetails]);

  const handleReset = () => {
    resetFillChanges();
    if (formRef.current) {
      formRef.current.change('dispensedQuantity', editablePrescriptionDetails.dispensedQuantity);
    }
  };

  const handleResolvePrescription = (formData) => {
    const overRidePrescriptionDetails = unMatchedFields(editablePrescriptionDetails, formData);
    const newFillDetails = pick(formData, overRidePrescriptionDetails);

    saveFillChanges(newFillDetails);
    onClose();
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Edit Current Fill</DialogTitle>

      <Form
        initialValues={{
          ...editablePrescriptionDetails,
          ...(payload?.prescriptionDetails || {}),
        }}
        onSubmit={handleResolvePrescription}
        render={({ handleSubmit, invalid, dirtyFields, form }) => {
          if (formRef.current !== form) {
            formRef.current = form;
          }

          return (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent sx={{ minWidth: 500 }}>
                <Typography variant='h6' gutterBottom sx={{ mt: -2, mb: 1, fontWeight: 600 }}>
                  Change Fulfillment Order Details
                </Typography>

                <Box
                  sx={{
                    mb: 2,
                    p: 1,
                    backgroundColor: 'info.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.main',
                  }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 500, color: 'white' }}>
                    Prescribed Quantity: {editablePrescriptionDetails.prescribedQuantity}
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'white', mt: 0.5, display: 'block' }}>
                    Note: The quantity in current fill cannot exceed the prescribed quantity
                  </Typography>
                </Box>

                {shippedDate && (
                  <Typography sx={{ mb: 2 }}>
                    Current Fill Shipped on: {format(new Date(shippedDate), 'MM/dd/yyyy')}
                  </Typography>
                )}

                {isReshipment && parentLineItemQuantity && (
                  <Typography sx={{ mb: 2 }}>Shipped quantity: {parentLineItemQuantity}</Typography>
                )}

                <TextField
                  id='EditFillModal-dispensed-quantity-input'
                  label='Quantity in Current Fill'
                  name='dispensedQuantity'
                  required
                  validations={[
                    validateGreaterThan('Quantity must be greater than 0', 0),
                    validateQuantities('dispensedQuantity'),
                    ...(isReshipment && parentLineItemQuantity
                      ? [
                          validateLessThanOrEqual(
                            'Reshipment quantity cannot be greater than the shipped quantity',
                            Number(parentLineItemQuantity)
                          ),
                        ]
                      : []),
                  ]}
                />
              </DialogContent>

              <DialogActions>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 'auto' }}>
                  {(canUndoFill || canRedoFill) && (
                    <>
                      <Tooltip title='Undo fill changes' arrow>
                        <span>
                          <IconButton
                            size='small'
                            onClick={undoFill}
                            disabled={!canUndoFill}
                            aria-label='Undo fill changes'
                          >
                            <UndoIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Redo fill changes' arrow>
                        <span>
                          <IconButton
                            size='small'
                            onClick={redoFill}
                            disabled={!canRedoFill}
                            aria-label='Redo fill changes'
                          >
                            <RedoIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Reset to initial state' arrow>
                        <span>
                          <IconButton
                            size='small'
                            onClick={handleReset}
                            aria-label='Reset fill changes'
                          >
                            <RefreshIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Box>
                <Button variant='outlined' color='secondary' onClick={onClose}>
                  Cancel
                </Button>
                <LoadingButton
                  type='submit'
                  variant='contained'
                  disabled={isEmpty(dirtyFields) || invalid}
                >
                  Confirm
                </LoadingButton>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

EditFillModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editablePrescriptionDetails: PropTypes.shape({
    refills: PropTypes.string,
    quantity: PropTypes.string,
    prescriberName: PropTypes.string,
    prescribedQuantity: PropTypes.string,
    dispensedQuantity: PropTypes.string,
  }).isRequired,
  shippedDate: PropTypes.string,
  isReshipment: PropTypes.bool.isRequired,
  parentLineItemQuantity: PropTypes.string,
};

EditFillModal.defaultProps = {
  shippedDate: null,
  parentLineItemQuantity: null,
};

export default EditFillModal;
